import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import DeleteConfirmModal from "./DeleteConfirmModal";
import { EditableTextCell, EditableNumberCell } from "./EditableCells";
import api from "../../utilities/api";

const columnHelper = createColumnHelper();

const StandardTable = () => {
  const [data, setData] = useState([]);
  const [standardCounts, setStandardCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([{ id: "sortOrder", desc: false }]);
  const [isCreating, setIsCreating] = useState(false);
  const [newStandardName, setNewStandardName] = useState("");
  const newStandardInputRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    standardId: null,
    standardName: "",
  });

  // Fetch standard usage counts
  const fetchStandardCounts = useCallback(async () => {
    try {
      const response = await api.get(`/standards/counts`);
      setStandardCounts(response.data);
    } catch (error) {
      console.error("Error fetching standard counts:", error);
    }
  }, []);

  // Fetch standards
  const fetchStandards = useCallback(async () => {
    try {
      setLoading(true);
      const [standardsResponse] = await Promise.all([
        api.get(`/standards`),
        fetchStandardCounts(),
      ]);
      setData(standardsResponse.data);
    } catch (error) {
      console.error("Error fetching standards:", error);
      toast.error("Failed to load standards");
    } finally {
      setLoading(false);
    }
  }, [fetchStandardCounts]);

  useEffect(() => {
    fetchStandards();
  }, [fetchStandards]);

  // Focus new standard input when creating
  useEffect(() => {
    if (isCreating && newStandardInputRef.current) {
      newStandardInputRef.current.focus();
    }
  }, [isCreating]);

  // Save field update
  const handleSave = useCallback(
    async (standardId, field, value) => {
      try {
        await api.patch(`/standards/${standardId}`, {
          [field]: value,
        });
        setData((prev) =>
          prev.map((standard) =>
            standard._id === standardId
              ? { ...standard, [field]: value }
              : standard,
          ),
        );
        toast.success("Standard updated");
      } catch (error) {
        console.error("Error updating standard:", error);
        toast.error(
          error.response?.data?.message || "Failed to update standard",
        );
        fetchStandards(); // Revert
      }
    },
    [fetchStandards],
  );

  // Create new standard
  const handleCreateStandard = useCallback(async () => {
    const name = newStandardName.trim();
    if (!name) {
      setIsCreating(false);
      setNewStandardName("");
      return;
    }

    try {
      const response = await api.post(`/standards`, {
        name,
        sortOrder: 0,
      });
      setData((prev) => [response.data, ...prev]);
      toast.success("Standard created");
    } catch (error) {
      console.error("Error creating standard:", error);
      toast.error(
        error.response?.data?.message || "Failed to create standard",
      );
    } finally {
      setIsCreating(false);
      setNewStandardName("");
    }
  }, [newStandardName]);

  // Delete standard
  const handleDelete = useCallback(async () => {
    const { standardId } = deleteModal;
    if (!standardId) return;

    try {
      await api.delete(`/standards/${standardId}`);
      setData((prev) =>
        prev.filter((standard) => standard._id !== standardId),
      );
      setStandardCounts((prev) => {
        const updated = { ...prev };
        delete updated[standardId];
        return updated;
      });
      toast.success("Standard deleted");
    } catch (error) {
      console.error("Error deleting standard:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete standard",
      );
      // Re-fetch counts so the UI reflects the current reality
      fetchStandardCounts();
    } finally {
      setDeleteModal({ isOpen: false, standardId: null, standardName: "" });
    }
  }, [deleteModal, fetchStandardCounts]);

  const editableTextColumn = useCallback(
    (id, header, size) =>
      columnHelper.accessor(id, {
        header,
        cell: (info) => (
          <EditableTextCell
            getValue={info.getValue}
            row={info.row}
            column={info.column}
            onSave={handleSave}
          />
        ),
        size,
      }),
    [handleSave],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "actions",
        header: () => (
          <button
            onClick={() => setIsCreating(true)}
            className="py-0 px-2 float-right mr-0 text-center text-cyan-900 font-bold text-lg border-2 border-gray-500 bg-gray-300 hover:bg-gray-500 hover:text-white rounded-lg transition duration-300"
          >
            New
          </button>
        ),
        cell: () => null,
        size: 80,
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => {
          const count = standardCounts[info.row.original._id] ?? 0;
          return (
            <span className="flex items-center">
              <EditableTextCell
                getValue={info.getValue}
                row={info.row}
                column={info.column}
                onSave={handleSave}
              />
              <span className="text-gray-400 text-xs whitespace-nowrap">
                ({count})
              </span>
            </span>
          );
        },
        size: 200,
      }),
      // No fixed size: Description takes the remaining width
      { ...editableTextColumn("description", "Description"), minSize: 300 },
      editableTextColumn("url", "URL", 250),
      columnHelper.accessor("sortOrder", {
        header: "Order",
        cell: (info) => (
          <EditableNumberCell
            getValue={info.getValue}
            row={info.row}
            column={info.column}
            onSave={handleSave}
          />
        ),
        size: 120,
      }),
      columnHelper.display({
        id: "delete",
        header: "",
        cell: ({ row }) => {
          const count = standardCounts[row.original._id] ?? 0;
          const inUse = count > 0;
          return (
            <button
              onClick={() =>
                setDeleteModal({
                  isOpen: true,
                  standardId: row.original._id,
                  standardName: row.original.name,
                })
              }
              disabled={inUse}
              title={
                inUse
                  ? `Cannot delete — used by ${count} record${count === 1 ? "" : "s"}`
                  : "Delete this standard"
              }
              className={`py-0 px-2 text-center font-normal text-sm border-2 rounded-lg transition duration-300 ${
                inUse
                  ? "text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed"
                  : "text-red-700 border-red-300 bg-white hover:bg-red-50 hover:border-red-500"
              }`}
            >
              Delete
            </button>
          );
        },
        size: 80,
      }),
    ],
    [handleSave, editableTextColumn, standardCounts],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    initialState: {
      pagination: { pageSize: 10 },
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Loading standards...
      </div>
    );
  }

  return (
    <div className="p-2 text-black fill-gray-600">
      <div className="mt-2 flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full text-left">
              <thead className="bg-cyan-900 text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                        }}
                        className="py-2 px-2 whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort() && (
                          <button
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <ArrowsUpDownIcon
                              className="h-6 w-6 text-white relative"
                              style={{ top: "6px" }}
                            />
                          </button>
                        )}
                        {
                          {
                            asc: "🔼",
                            desc: "🔽",
                          }[header.column.getIsSorted()]
                        }
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {/* Inline new standard row */}
                {isCreating && (
                  <tr className="bg-cyan-50 border-b-2 border-cyan-300">
                    <td className="py-1 px-2"></td>
                    <td className="py-1 px-2">
                      <input
                        ref={newStandardInputRef}
                        type="text"
                        value={newStandardName}
                        onChange={(e) => setNewStandardName(e.target.value)}
                        onBlur={handleCreateStandard}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") {
                            setIsCreating(false);
                            setNewStandardName("");
                          }
                        }}
                        placeholder="Enter standard name..."
                        className="bg-white border border-cyan-400 rounded px-2 py-1 w-full text-sm focus:outline-none focus:border-cyan-600"
                      />
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2"></td>
                  </tr>
                )}
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i % 2 === 0 ? "bg-slate-100" : "bg-slate-300"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="py-1 px-2">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="text-center h-32">
                    <td colSpan={columns.length}>No standards found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={() => table.firstPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<<"}
        </button>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<"}
        </button>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">"}
        </button>
        <button
          onClick={() => table.lastPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">>"}
        </button>
        <span className="flex items-center gap-1">
          <input
            type="number"
            value={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border text-right rounded w-14 bg-transparent"
          />{" "}
          of {table.getPageCount()}
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="p-2 bg-transparent"
        >
          {[10, 25, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, standardId: null, standardName: "" })
        }
        onConfirm={handleDelete}
        title="Delete Standard"
        message={`Are you sure you want to delete "${deleteModal.standardName}"?`}
      />
    </div>
  );
};

export default StandardTable;
